/**
 * HealthCheckCard — the BIG GREEN BUTTON on Admin → System.
 *
 * One-click pre-deploy regression check. Founder clicks it before clicking
 * the Emergent "Deploy" button. Result is shown right here in the admin
 * panel — green checkmark + "Safe to deploy" or a clear red list of what
 * broke.
 *
 * Backend endpoint: POST /api/admin/health-check (admin auth)
 *
 * Only runs READ-ONLY regression tests so it's safe to click on production
 * without polluting the database.
 */
import React, { useState } from 'react';
import {
  ShieldCheck, Loader2, AlertTriangle, CheckCircle2, XCircle,
  Activity, Clock,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function HealthCheckCard({ adminToken }) {
  const [running, setRunning] = useState(false);
  const [result, setResult]   = useState(null);
  const [err, setErr]         = useState(null);
  const [showRaw, setShowRaw] = useState(false);

  const run = async () => {
    setRunning(true);
    setErr(null);
    setResult(null);
    setShowRaw(false);
    try {
      const res = await fetch(`${API}/api/admin/health`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${adminToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `Health check failed: ${res.status}`);
      }
      setResult(await res.json());
    } catch (e) {
      setErr(e.message);
    } finally {
      setRunning(false);
    }
  };

  const overall = result?.overallStatus || result?.status;
  const isPass = overall === 'pass';
  const isWarn = overall === 'warn';
  const isFail = overall === 'fail' || overall === 'error';
  const pytestFailed = result && (result.status === 'fail' || result.status === 'error');

  return (
    <div
      data-testid="health-check-card"
      className={`border-2 rounded-xl p-5 space-y-4 transition-colors ${
        isPass ? 'bg-emerald-50 border-emerald-300'
        : isWarn ? 'bg-amber-50 border-amber-300'
        : isFail ? 'bg-rose-50 border-rose-300'
        : 'bg-gradient-to-br from-sky-50 to-blue-50 border-sky-200'
      }`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`text-white rounded-lg p-2.5 ${
            isPass ? 'bg-emerald-600'
            : isWarn ? 'bg-amber-600'
            : isFail ? 'bg-rose-600'
            : 'bg-sky-600'
          }`}
        >
          <ShieldCheck className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <h3 className="text-base font-extrabold text-slate-900">
            Pre-Deploy Health Check
          </h3>
          <p className="text-xs text-slate-700 mt-0.5 max-w-3xl leading-snug">
            Click <strong>Run Health Check</strong> before clicking Deploy on the
            Emergent dashboard. If you see a green checkmark, you&apos;re safe to deploy.
            Takes about 30–45 seconds. Only runs read-only checks — safe to click anytime.
          </p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <button
          onClick={run}
          disabled={running}
          data-testid="health-check-run-btn"
          className={`inline-flex items-center justify-center gap-2 font-extrabold text-base px-6 py-3 rounded-xl shadow-md transition-all ${
            running
              ? 'bg-slate-300 text-white cursor-wait'
              : 'bg-sky-600 hover:bg-sky-700 active:bg-sky-800 text-white hover:shadow-lg'
          }`}
        >
          {running ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Running… (about 45 seconds)
            </>
          ) : (
            <>
              <Activity className="w-5 h-5" />
              Run Health Check
            </>
          )}
        </button>

        {result && (
          <div className="flex items-center gap-2 text-xs text-slate-600"
               data-testid="health-check-summary">
            <Clock className="w-3.5 h-3.5" />
            Took {result.durationSec}s · {result.passed}/{result.total} tests passed
          </div>
        )}
      </div>

      {err && (
        <div
          className="bg-rose-100 border border-rose-300 text-rose-900 text-sm rounded-lg p-3 flex items-start gap-2"
          data-testid="health-check-error"
        >
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-bold">Health check failed to run</p>
            <p className="text-xs mt-1">{err}</p>
          </div>
        </div>
      )}

      {result?.systemChecks && result.systemChecks.length > 0 && (
        <div data-testid="health-check-system" className="space-y-2">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-600">
            Live system checks
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {result.systemChecks.map((c, i) => {
              const dot = c.status === 'pass' ? 'bg-emerald-500'
                : c.status === 'warn' ? 'bg-amber-500'
                : 'bg-rose-500';
              const ring = c.status === 'pass' ? 'border-emerald-200'
                : c.status === 'warn' ? 'border-amber-200'
                : 'border-rose-200';
              return (
                <div
                  key={i}
                  className={`bg-white border ${ring} rounded-lg p-3 flex items-start gap-2`}
                  data-testid={`health-check-system-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                >
                  <span className={`mt-1.5 inline-block w-2 h-2 rounded-full ${dot} flex-shrink-0`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-slate-900">{c.name}</p>
                    <p className="text-xs text-slate-600 break-words">{c.message}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {isPass && (
        <div
          className="bg-emerald-100 border-2 border-emerald-400 rounded-lg p-4 flex items-start gap-3"
          data-testid="health-check-pass"
        >
          <CheckCircle2 className="w-7 h-7 text-emerald-700 flex-shrink-0" />
          <div>
            <p className="text-lg font-extrabold text-emerald-900">
              ✓ All systems healthy — Safe to deploy
            </p>
            <p className="text-sm text-emerald-800 mt-1">
              All {result.passed} regression checks passed AND all live system probes are green.
              You can click Deploy on the Emergent dashboard with confidence.
            </p>
          </div>
        </div>
      )}

      {isWarn && (
        <div
          className="bg-amber-100 border-2 border-amber-400 rounded-lg p-4 flex items-start gap-3"
          data-testid="health-check-warn"
        >
          <AlertTriangle className="w-7 h-7 text-amber-700 flex-shrink-0" />
          <div>
            <p className="text-lg font-extrabold text-amber-900">
              ⚠ Tests pass — but live system has warnings
            </p>
            <p className="text-sm text-amber-800 mt-1">
              All {result.passed} regression checks passed, but one or more live system probes
              raised a warning above. Review them before deploying — most are non-blocking
              (e.g. App Settings not fully populated yet), but check carefully.
            </p>
          </div>
        </div>
      )}

      {isFail && (
        <div
          className="bg-rose-100 border-2 border-rose-400 rounded-lg p-4 space-y-3"
          data-testid="health-check-fail"
        >
          <div className="flex items-start gap-3">
            <XCircle className="w-7 h-7 text-rose-700 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-lg font-extrabold text-rose-900">
                {pytestFailed
                  ? `✗ ${result.failed + (result.errors || 0)} regression check${(result.failed + (result.errors || 0)) !== 1 ? 's' : ''} failed — Do NOT deploy`
                  : '✗ Live system check failed — Do NOT deploy'}
              </p>
              <p className="text-sm text-rose-800 mt-1">
                {pytestFailed
                  ? 'Send a screenshot of the failures below to your developer / Emergent agent before deploying. Deploying now would push these bugs to production.'
                  : 'A critical live system check failed (see red dot above). Fix it before deploying — deploying now would push something broken to production.'}
              </p>
            </div>
          </div>
          {result.failures && result.failures.length > 0 && (
            <ul className="space-y-2 text-sm" data-testid="health-check-failures">
              {result.failures.map((f, i) => (
                <li
                  key={i}
                  className="bg-white border border-rose-200 rounded p-3"
                  data-testid={`health-check-failure-${i}`}
                >
                  <p className="font-mono text-[11px] text-rose-700 break-all">
                    {f.nodeid}
                  </p>
                  {f.message && (
                    <p className="text-xs text-rose-900 mt-1 leading-snug">
                      {f.message}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {result && (
        <div className="pt-2 border-t border-slate-200">
          <button
            onClick={() => setShowRaw(s => !s)}
            className="text-xs text-slate-500 hover:text-slate-700 underline"
            data-testid="health-check-toggle-raw"
          >
            {showRaw ? 'Hide raw output' : 'Show raw output (advanced)'}
          </button>
          {showRaw && (
            <pre
              data-testid="health-check-raw"
              className="mt-2 bg-slate-900 text-slate-100 text-[11px] p-3 rounded overflow-x-auto max-h-72 leading-relaxed"
            >
              {result.rawOutput}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}
